/**
 * Finance Manager Billing Panel — v28-3-4
 *
 * Route: /org/billing
 * Access: finance_manager + org_admin only
 *
 * Sections:
 * 1. Agreement Summary (pro/premium seats, rates, billing type/cycle/contact)
 * 2. Seat Usage Breakdown (expandable: pro, premium, non-billable)
 * 3. Invoices Table (download PDF, mark EFT invoices as paid)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, FileText, CheckCircle } from 'lucide-react';
import { api } from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MemberSeatDetail {
  user_id: number;
  name: string;
  email: string;
  roles: string[];
}

interface AgreementDetail {
  pro_seats: number;
  premium_seats: number;
  seat_rate_pro_usd: number;
  seat_rate_premium_usd: number;
  billing_type: string | null;
  billing_cycle: string | null;
  billing_contact_email: string | null;
  next_billing_date: string | null;
}

interface SeatUsageDetail {
  pro_used: number;
  pro_pending: number;
  premium_used: number;
  premium_pending: number;
  non_billable_count: number;
  pro_members: MemberSeatDetail[];
  premium_members: MemberSeatDetail[];
  non_billable_members: MemberSeatDetail[];
}

interface OrgBillingData {
  agreement: AgreementDetail | null;
  seats: SeatUsageDetail;
}

interface Invoice {
  id: string;
  period_label: string;
  pro_seats: number;
  premium_seats: number;
  amount_usd: string;
  due_date: string | null;
  status: string;
  billing_type: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(val: number): string {
  return `$${val.toFixed(2)}`;
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'paid'
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      : status === 'overdue'
      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      : status === 'sent'
      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {capitalize(status)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CollapsibleSection({
  title,
  count,
  members,
}: {
  title: string;
  count: number;
  members: MemberSeatDetail[];
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          {title}
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({count})</span>
        </span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
          {members.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">None</p>
          ) : (
            members.map((m) => (
              <div key={m.user_id} className="px-4 py-2 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{m.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{m.email}</p>
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  {m.roles.map((r) => (
                    <span
                      key={r}
                      className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs"
                    >
                      {r.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function OrgBilling() {
  const queryClient = useQueryClient();
  const [markPaidError, setMarkPaidError] = useState('');
  const [pdfError, setPdfError] = useState('');

  const {
    data: billing,
    isLoading: billingLoading,
    error: billingError,
  } = useQuery<OrgBillingData>({
    queryKey: ['org-billing'],
    queryFn: () => api.get('/orgs/billing').then((r) => r.data),
  });

  const {
    data: invoices,
    isLoading: invoicesLoading,
  } = useQuery<Invoice[]>({
    queryKey: ['org-invoices'],
    queryFn: () => api.get('/orgs/invoices').then((r) => r.data),
    initialData: [],
  });

  const markPaidMutation = useMutation({
    mutationFn: ({ invoiceId, billingType }: { invoiceId: string; billingType: string }) =>
      api.post(`/orgs/invoices/${invoiceId}/mark-paid`, { billing_type: billingType }),
    onSuccess: () => {
      setMarkPaidError('');
      queryClient.invalidateQueries({ queryKey: ['org-invoices'] });
    },
    onError: (err: any) => {
      setMarkPaidError(err?.response?.data?.detail || 'Failed to mark invoice as paid');
    },
  });

  const handleDownloadPdf = async (invoiceId: string) => {
    setPdfError('');
    try {
      const resp = await api.get(`/orgs/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setPdfError('Failed to download invoice PDF. Please try again.');
    }
  };

  if (billingLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (billingError) {
    return (
      <div className="p-6 text-center text-yellow-600 dark:text-yellow-400">
        Billing panel is only available to Finance Managers and Org Admins.
      </div>
    );
  }

  const agreement = billing?.agreement ?? null;
  const seats = billing?.seats;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Billing</h1>

      {/* Section 1: Agreement Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Current Agreement
        </h2>
        {agreement ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">Pro seats</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {seats?.pro_used ?? 0} used / {agreement.pro_seats} total
                  <span className="ml-2 text-gray-500">@ {fmt(agreement.seat_rate_pro_usd)}/seat/month</span>
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Premium seats</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {seats?.premium_used ?? 0} used / {agreement.premium_seats} total
                  <span className="ml-2 text-gray-500">@ {fmt(agreement.seat_rate_premium_usd)}/seat/month</span>
                </p>
              </div>
              {agreement.billing_type && (
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Billing type</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {agreement.billing_type.toUpperCase()}
                  </p>
                </div>
              )}
              {agreement.billing_cycle && (
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Billing cycle</p>
                  <p className="font-medium text-gray-900 dark:text-white capitalize">
                    {agreement.billing_cycle}
                  </p>
                </div>
              )}
              {agreement.next_billing_date && (
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Next bill date</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {agreement.next_billing_date}
                  </p>
                </div>
              )}
              {agreement.billing_contact_email && (
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Billing contact</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {agreement.billing_contact_email}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No active pricing agreement found.
          </p>
        )}
      </div>

      {/* Section 2: Seat Usage Breakdown */}
      {seats && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Seat Usage Breakdown
          </h2>
          <div className="space-y-3">
            <CollapsibleSection
              title="Pro members"
              count={seats.pro_used}
              members={seats.pro_members}
            />
            <CollapsibleSection
              title="Premium members"
              count={seats.premium_used}
              members={seats.premium_members}
            />
            <CollapsibleSection
              title="Non-billable staff"
              count={seats.non_billable_count}
              members={seats.non_billable_members}
            />
          </div>
        </div>
      )}

      {/* Section 3: Invoices Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Invoices</h2>
        {markPaidError && (
          <p className="text-sm text-red-500 mb-3">{markPaidError}</p>
        )}
        {pdfError && (
          <p className="text-sm text-red-500 mb-3">{pdfError}</p>
        )}
        {invoicesLoading ? (
          <p className="text-sm text-gray-500">Loading invoices…</p>
        ) : !invoices || invoices.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No invoices found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="pb-2 font-medium">Period</th>
                  <th className="pb-2 font-medium">Pro</th>
                  <th className="pb-2 font-medium">Premium</th>
                  <th className="pb-2 font-medium">Amount</th>
                  <th className="pb-2 font-medium">Due date</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                {invoices.map((inv) => {
                  const canMarkPaid =
                    inv.billing_type === 'eft' &&
                    (inv.status === 'sent' || inv.status === 'overdue');
                  return (
                    <tr key={inv.id}>
                      <td className="py-3 text-gray-900 dark:text-white">{inv.period_label}</td>
                      <td className="py-3 text-gray-600 dark:text-gray-300">{inv.pro_seats}</td>
                      <td className="py-3 text-gray-600 dark:text-gray-300">{inv.premium_seats}</td>
                      <td className="py-3 font-medium text-gray-900 dark:text-white">
                        ${parseFloat(inv.amount_usd).toFixed(2)}
                      </td>
                      <td className="py-3 text-gray-600 dark:text-gray-300">
                        {inv.due_date ?? '—'}
                      </td>
                      <td className="py-3">
                        <StatusBadge status={inv.status} />
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownloadPdf(inv.id)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            title="Download PDF"
                          >
                            <FileText className="w-3 h-3" />
                            PDF
                          </button>
                          {canMarkPaid && (
                            <button
                              onClick={() => markPaidMutation.mutate({ invoiceId: inv.id, billingType: inv.billing_type ?? 'eft' })}
                              disabled={markPaidMutation.isPending}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded hover:bg-green-200 dark:hover:bg-green-900/50 disabled:opacity-50 transition-colors"
                              title="Mark as paid"
                            >
                              <CheckCircle className="w-3 h-3" />
                              Mark paid
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
